import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type {
  ThematicNode,
  ThematicNodeWithEntity,
  NewThematicNode,
  UpdateThematicNode,
} from '@/types/thematicMaps';
import { normalizeToTokens, inferQueryFrameFromQuestion } from '@/utils/textNormalization';
import { slugify } from '@/lib/slugify';

export const useThematicNodes = (mapId: string | undefined) => {
  const [nodes, setNodes] = useState<ThematicNodeWithEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchNodes = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from('thematic_nodes')
        .select(`
          *,
          thematic_entities!thematic_nodes_primary_entity_id_fkey(canonical_name, entity_type),
          thematic_seeds!inner(name)
        `)
        .eq('map_id', mapId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // Spread ALL columns from DB — don't manually map (same fix as useThematicNodeContent)
      const nodesWithEntity: ThematicNodeWithEntity[] = (data || []).map((n: any) => ({
        ...n,
        primary_intent: n.primary_intent || '',
        primary_entity_raw: n.primary_entity_raw || '',
        required_entities_raw: n.required_entities_raw || [],
        required_entity_ids: n.required_entity_ids || [],
        map_version: n.map_version || 'v1',
        primary_entity: n.thematic_entities || undefined,
        seed_name: n.thematic_seeds?.name,
      }));

      setNodes(nodesWithEntity);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania nodes';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createNode = useCallback(async (
    nodeData: Omit<NewThematicNode, 'user_id' | 'map_id'>
  ): Promise<ThematicNode | null> => {
    if (!user || !mapId) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('thematic_nodes')
        .insert({
          user_id: user.id,
          map_id: mapId,
          ...nodeData,
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: 'Sukces', description: 'Node dodany' });
      await fetchNodes();
      return data as ThematicNode;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia node';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, mapId, toast, fetchNodes]);

  const createNodesBulk = useCallback(async (
    nodesData: Omit<NewThematicNode, 'user_id' | 'map_id'>[]
  ): Promise<ThematicNode[]> => {
    if (!user || !mapId) return [];

    try {
      const rows = nodesData.map((n, i) => {
        const question = (n as { canonical_question?: string }).canonical_question || (n as { title?: string }).title || "";
        return {
          user_id: user.id,
          map_id: mapId,
          sort_order: i,
          ...n,
          // M9: pre-computed tokens + query_frame (bez LLM, czysto kodowo)
          canonical_question_tokens_normalized: normalizeToTokens(question),
          query_frame: inferQueryFrameFromQuestion(question),
        };
      });

      // In-memory pre-filter (optymalizacja — odrzuca oczywiste duplikaty
      // zanim plyna do DB). Dziala TYLKO gdy rows maja canonical_question_hash;
      // jesli brak hasha → rzad i tak moze byc duplikatem, ale upsert nizej to zlapie.
      const existingHashes = new Set(
        nodes.map((n) => n.canonical_question_hash).filter(Boolean),
      );
      const filteredRows = rows.filter((r: any) =>
        !r.canonical_question_hash || !existingHashes.has(r.canonical_question_hash),
      );

      if (filteredRows.length === 0) {
        toast({ title: 'Info', description: 'Wszystkie nodes już istnieją' });
        return [];
      }

      // Slug pre-dedup: DB ma drugi unique constraint `uq_node_slug_per_map`
      // (map_id, slug). UPSERT lapie tylko jeden onConflict, wiec slug-collision
      // wali `23505`. Tutaj ZANIM wyslemy do DB — suffix -2/-3/... na kazdym
      // kolidujacym slugu (z istniejacymi w DB + same-batch). Zero DB conflict.
      const takenSlugs = new Set(nodes.map((n) => n.slug).filter(Boolean));
      let renamedCount = 0;
      const newRows = filteredRows.map((r: any) => {
        if (!r.slug) return r;
        // FIX (2026-06-09): slug ASCII (WP-zgodny). LLM bywa zwraca slug
        // z polskimi znakami → slugify do ASCII PRZED collision check
        // (DB ma unique index uq_node_slug_per_map). Base do suffix -2/-3
        // używa slug PO slugify.
        let slug = slugify(r.slug);
        if (takenSlugs.has(slug)) {
          const base = slug;
          let n = 2;
          while (takenSlugs.has(`${base}-${n}`)) n++;
          slug = `${base}-${n}`;
          renamedCount++;
        }
        takenSlugs.add(slug);
        return { ...r, slug };
      });

      console.log('[createNodesBulk] upserting rows:', newRows.length, 'renamed_slugs:', renamedCount, 'sample keys:', Object.keys(newRows[0] || {}));
      // UPSERT z ignoreDuplicates — jesli hash byl undefined w pre-filter albo wyscig
      // z innym INSERT-em (np. worker), unique constraint `uq_node_question_per_map`
      // powoduje skip zamiast rzucic 23505 na cala transakcje.
      const { data, error } = await (supabase as any)
        .from('thematic_nodes')
        .upsert(newRows, {
          onConflict: 'map_id,canonical_question_hash',
          ignoreDuplicates: true,
        })
        .select();

      if (error) {
        console.error('[createNodesBulk] Supabase error:', JSON.stringify(error, null, 2));
        console.error('[createNodesBulk] First row:', JSON.stringify(newRows[0], null, 2));
        throw error;
      }

      const insertedCount = data?.length || 0;
      const skippedCount = newRows.length - insertedCount;
      const parts = [`Dodano ${insertedCount} nodes`];
      if (skippedCount > 0) parts.push(`${skippedCount} duplikatow pominietych`);
      if (renamedCount > 0) parts.push(`${renamedCount} slugow przemianowanych`);
      toast({
        title: 'Sukces',
        description: parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(', ')})` : parts[0],
      });
      await fetchNodes();
      return (data || []) as ThematicNode[];
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd dodawania nodes';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return [];
    }
  }, [user, mapId, toast, fetchNodes]);

  const updateNode = useCallback(async (id: string, updates: UpdateThematicNode): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_nodes')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setNodes(prev => prev.map(n =>
        n.id === id ? { ...n, ...updates } : n
      ));
      toast({ title: 'Sukces', description: 'Node zaktualizowany' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji node';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteNode = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_nodes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setNodes(prev => prev.filter(n => n.id !== id));
      toast({ title: 'Sukces', description: 'Node usunięty' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania node';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchNodes();
    }
  }, [user?.id, mapId, fetchNodes]);

  const { isLive } = useRealtimeSubscription({
    channel: `thematic_nodes_${mapId || 'none'}`,
    table: 'thematic_nodes',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchNodes,
  });

  return {
    nodes,
    loading,
    isLive,
    createNode,
    createNodesBulk,
    updateNode,
    deleteNode,
    refreshNodes: fetchNodes,
  };
};
